import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Menu, X, ChevronDown,
    Layers, Receipt, Calculator, TrendingUp, Sparkles, Users,
    Beaker, Building2, Plug, LineChart, HelpCircle, CalendarCheck
} from "lucide-react";
import { Logo } from "../../../components/common/Logo";
import { isForecastingEnabled } from "@/lib/features";

interface LandingNavProps {
    onLogin: () => void;
    onRegister: () => void;
}

interface MegaItem {
    iconNode: React.ReactNode;
    titleKey: string;
    descKey: string;
    target: string; // anchor id (with #) or route path
    accent: "primary" | "purple" | "blue" | "green" | "amber" | "rose";
}

interface NavEntry {
    id: string;
    labelKey: string;
    items?: MegaItem[];
    target?: string;
}

const ACCENT: Record<MegaItem["accent"], string> = {
    primary: "bg-accent-primary/10 text-accent-primary",
    purple: "bg-purple-100 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400",
    blue: "bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400",
    green: "bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-500",
    amber: "bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-500",
    rose: "bg-rose-100 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

const getNavEntries = (): NavEntry[] => {
    const forecasting = isForecastingEnabled();
    return [
        {
            id: "product",
            labelKey: "landing.nav.product",
            items: [
                { iconNode: <Layers size={18} />, titleKey: "landing.feature.channelsTitle", descKey: "landing.feature.channelsDesc", target: "#features", accent: "primary" },
                { iconNode: <Receipt size={18} />, titleKey: "landing.feature.journalsTitle", descKey: "landing.feature.journalsDesc", target: "#features", accent: "rose" },
                { iconNode: <Calculator size={18} />, titleKey: "landing.feature.formulasTitle", descKey: "landing.feature.formulasDesc", target: "#features", accent: "blue" },
                ...(forecasting ? [{ iconNode: <TrendingUp size={18} />, titleKey: "landing.feature.forecastTitle", descKey: "landing.feature.forecastDesc", target: "#features", accent: "green" as const }] : []),
                { iconNode: <Sparkles size={18} />, titleKey: "landing.feature.aiTitle", descKey: "landing.feature.aiDesc", target: "#features", accent: "purple" },
                { iconNode: <Users size={18} />, titleKey: "landing.feature.sharingTitle", descKey: "landing.feature.sharingDesc", target: "#features", accent: "amber" },
            ],
        },
        {
            id: "solutions",
            labelKey: "landing.nav.solutions",
            items: [
                { iconNode: <Beaker size={18} />, titleKey: "landing.persona.test.tab", descKey: "landing.nav.solutionsTestDesc", target: "#personas", accent: "amber" },
                { iconNode: <TrendingUp size={18} />, titleKey: "landing.persona.scale.tab", descKey: forecasting ? "landing.nav.solutionsScaleDesc" : "landing.nav.solutionsScaleDescNoForecast", target: "#personas", accent: "primary" },
                { iconNode: <Building2 size={18} />, titleKey: "landing.persona.brand.tab", descKey: "landing.nav.solutionsBrandDesc", target: "#personas", accent: "blue" },
            ],
        },
        {
            id: "resources",
            labelKey: "landing.nav.resources",
            items: [
                { iconNode: <Plug size={18} />, titleKey: "landing.nav.resourcesHowTitle", descKey: "landing.nav.resourcesHowDesc", target: "#how", accent: "primary" },
                { iconNode: <LineChart size={18} />, titleKey: "landing.nav.resourcesIntegrationsTitle", descKey: "landing.nav.resourcesIntegrationsDesc", target: "#integrations", accent: "blue" },
                { iconNode: <HelpCircle size={18} />, titleKey: "landing.nav.resourcesFaqTitle", descKey: "landing.nav.resourcesFaqDesc", target: "#faq", accent: "purple" },
                { iconNode: <CalendarCheck size={18} />, titleKey: "landing.nav.resourcesDemoTitle", descKey: "landing.nav.resourcesDemoDesc", target: "/demo", accent: "rose" },
            ],
        },
        {
            id: "pricing",
            labelKey: "landing.nav.pricing",
            target: "/pricing",
        },
    ];
};

export const LandingNav = ({ onLogin, onRegister }: LandingNavProps) => {
    const { t } = useTranslation("common");
    const nav = getNavEntries();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileSection, setMobileSection] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const [hovering, setHovering] = useState<string | null>(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const followTarget = (target: string) => {
        setMobileOpen(false);
        setHovering(null);
        if (target.startsWith("#")) {
            const id = target.slice(1);
            // If we're not on the landing page, route home then scroll
            if (window.location.pathname !== "/") {
                navigate("/" + target);
                return;
            }
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(target);
        }
    };

    return (
        <nav
            className={`fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b transition-colors duration-200 ${scrolled
                ? "bg-white/85 dark:bg-neutral-900/85 border-neutral-200/80 dark:border-neutral-800/80"
                : "bg-white/40 dark:bg-neutral-900/40 border-transparent"
                }`}
            onMouseLeave={() => setHovering(null)}
        >
            <div className="container mx-auto px-4 sm:px-6 h-16 sm:h-18 flex items-center justify-between gap-4">
                <button onClick={() => followTarget("#top")} className="flex items-center gap-2 shrink-0">
                    <Logo className="w-6 h-6" />
                    <span className="text-xl font-medium font-display text-text-primary tracking-tight">
                        {t("brandName")}
                    </span>
                </button>

                {/* Desktop nav */}
                <div className="hidden lg:flex items-center gap-1 relative">
                    {nav.map((entry) => {
                        const hasMenu = !!entry.items;
                        const isOpen = hovering === entry.id;
                        return (
                            <div
                                key={entry.id}
                                className="relative"
                                onMouseEnter={() => hasMenu ? setHovering(entry.id) : setHovering(null)}
                            >
                                <button
                                    onClick={() => entry.target ? followTarget(entry.target) : null}
                                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md ${isOpen
                                        ? "text-text-primary dark:text-white"
                                        : "text-neutral-600 dark:text-neutral-300 hover:text-text-primary dark:hover:text-white"
                                        }`}
                                    aria-expanded={isOpen}
                                    aria-haspopup={hasMenu}
                                >
                                    <span>{t(entry.labelKey)}</span>
                                    {hasMenu && <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={onLogin}
                        className="hidden sm:block text-sm font-semibold text-text-primary dark:text-neutral-200 hover:text-accent-primary transition-colors"
                    >
                        {t("auth.login")}
                    </button>
                    <button
                        onClick={() => followTarget("/demo")}
                        className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-text-primary dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                        <CalendarCheck size={15} />
                        {t("landing.cta.bookDemo")}
                    </button>
                    <button
                        onClick={onRegister}
                        className="bg-text-primary dark:bg-white text-white dark:text-neutral-900 px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
                    >
                        {t("landing.cta.startFree")}
                    </button>
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        className="lg:hidden p-2 text-text-primary dark:text-neutral-200 hover:text-accent-primary transition-colors"
                        aria-label={t("landing.nav.menu") as string}
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {/* Desktop mega-menu panel — single floating panel under the active group */}
            {hovering && (
                <div
                    className="hidden lg:block absolute left-0 right-0 top-full"
                    onMouseEnter={() => setHovering(hovering)}
                >
                    <div className="container mx-auto px-4 sm:px-6 pb-2">
                        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-xl mt-2 p-6 max-w-3xl mx-auto">
                            {nav.find(n => n.id === hovering)?.items && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {nav.find(n => n.id === hovering)!.items!.map((item) => (
                                        <button
                                            key={item.titleKey + item.target}
                                            onClick={() => followTarget(item.target)}
                                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 text-left transition-colors group"
                                        >
                                            <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${ACCENT[item.accent]}`}>
                                                {item.iconNode}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-sm font-bold text-text-primary group-hover:text-accent-primary transition-colors">
                                                    {t(item.titleKey)}
                                                </span>
                                                <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
                                                    {t(item.descKey)}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="lg:hidden border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 max-h-[80vh] overflow-y-auto">
                    <div className="container mx-auto px-4 py-3 flex flex-col gap-1">
                        {nav.map((entry) => {
                            const hasMenu = !!entry.items;
                            const isOpen = mobileSection === entry.id;
                            if (!hasMenu) {
                                return (
                                    <button
                                        key={entry.id}
                                        onClick={() => entry.target && followTarget(entry.target)}
                                        className="text-left px-3 py-2.5 text-sm font-bold text-text-primary dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
                                    >
                                        {t(entry.labelKey)}
                                    </button>
                                );
                            }
                            return (
                                <div key={entry.id} className="rounded-md overflow-hidden">
                                    <button
                                        onClick={() => setMobileSection(isOpen ? null : entry.id)}
                                        className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-text-primary dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                    >
                                        <span>{t(entry.labelKey)}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    {isOpen && entry.items && (
                                        <div className="pl-2 pb-2 space-y-1">
                                            {entry.items.map((item) => (
                                                <button
                                                    key={item.titleKey + item.target}
                                                    onClick={() => followTarget(item.target)}
                                                    className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                                >
                                                    <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${ACCENT[item.accent]}`}>
                                                        {item.iconNode}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-bold text-text-primary">
                                                            {t(item.titleKey)}
                                                        </span>
                                                        <span className="block text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2">
                                                            {t(item.descKey)}
                                                        </span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <div className="h-px bg-neutral-200 dark:bg-neutral-800 my-2" />

                        <button
                            onClick={() => { setMobileOpen(false); onLogin(); }}
                            className="text-left px-3 py-2.5 text-sm font-bold text-text-primary dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
                        >
                            {t("auth.login")}
                        </button>
                        <button
                            onClick={() => followTarget("/demo")}
                            className="text-left px-3 py-2.5 text-sm font-bold text-text-primary dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors flex items-center gap-2"
                        >
                            <CalendarCheck size={15} />
                            {t("landing.cta.bookDemo")}
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
};
